const { jQuery: $ } = window;

import FilterControl from "./FilterControl";

/**
 * The default filter control: a `<select>` upgraded to select2. Reproduces the plugin's original filter
 * behavior exactly — this is what every existing (`data-filter-type`-less) filter resolves to.
 *
 * The value read/write/observe methods are inherited from FilterControl (they operate on the native
 * `<select>`, which select2 keeps in sync). Only setup is type-specific: applying select2 with the
 * per-filter config, and — for taxonomy filters — narrowing the term list to the active post types.
 */
export default class SelectFilter extends FilterControl {
    /** @member {object|boolean} The resolved select2 config (false = select2 disabled for this filter). */
    #config = null;

    /** @member {object} Live per-option count map ({ value: count }); read by templateResult. */
    #counts = {};

    /** @member {boolean} Whether a counts payload has ever arrived. Distinguishes "counts not loaded yet"
     *  (disable nothing) from "counts loaded" — where an empty map is a valid all-zero result (disable all). */
    #countsReceived = false;

    /**
     * Applies select2 (unless disabled via the `select2_options_<name>` filter returning false) and wires
     * the taxonomy term-narrowing fetch.
     */
    init() {
        const instance = this.instance;
        const services = instance.services;
        const $select = this.control();
        const name = this.name;

        const config = services.hooks.applyFilter(`select2_options_${name}`, services.select2Opts, $select, instance.$el);

        if(config === false) {
            this.#config = false;
            return;
        }

        // templateResult reads the LIVE count map at render time, so "(N)" labels survive select2's own
        // re-render and the applyFilterData option re-seed (both rebuild <option>s). Zero-count options are
        // disabled at the <option> level (see #applyDisabled) and rendered greyed.
        this.#config = $.extend({}, config, {
            templateResult: (option) => this.#formatResult(option),
        });

        $select.select2(this.#config);

        // Taxonomy narrowing + counts arrive via the instance's batch /filters request → applyFilterData().
    }

    /**
     * Renders a dropdown result with its count suffix, reading the live map so it survives re-renders.
     * @param {object} option select2 result data ({ id, text }).
     * @returns {string}
     */
    #formatResult(option) {
        if(!option.id) {
            return option.text; // placeholder / "All" — no count
        }

        const count = this.#counts[String(option.id)];
        return count === undefined ? option.text : `${option.text} (${count})`;
    }

    /**
     * Disables (greys) every zero-count option, enables the rest. No-op until a counts payload has arrived
     * (see #countsReceived); after that an empty map is honored (all terms zero → all disabled).
     */
    #applyDisabled() {
        this.control().find("option").each((i, el) => {
            if(el.value === "") {
                return;
            }
            el.disabled = this.#countsReceived && (this.#counts[String(el.value)] ?? 0) === 0;
        });
    }

    /**
     * Applies batch filter-data for this select:
     *  - `data.options` ({value:label}) — re-seeds the term list (narrowed to the active post types) into
     *    select2's {id,text} shape, preserving the current selection and the widget config.
     *  - `data.counts` ({value:count}) — sets the live count map and disables zero-count options (a
     *    present-but-empty map disables all; a payload with no `counts` key leaves disabled state untouched).
     *
     * @param {object} data { options?: {value=>label}, counts?: {value=>count} }
     */
    applyFilterData(data) {
        data = data || {};

        if(this.#config === false) {
            return; // select2 disabled for this filter — nothing to re-seed/render
        }

        if(data.options) {
            const $select = this.control();
            const selected = $select.val();

            const seedData = Object.entries(data.options).map(([id, text]) => ({ id, text }));
            $select.select2($.extend({}, this.#config, { data: seedData }));

            // Re-seeding rebuilds the <option>s; restore the prior selection. The single change.select2
            // below syncs the widget (it does NOT fire the reload handler) — so we don't trigger it here too.
            if(selected && selected.length) {
                $select.val(selected);
            }
        }

        if(data.counts) {
            this.#counts = data.counts;
            this.#countsReceived = true;
            this.#applyDisabled();
        }

        // Refresh the widget's rendering once — reflects any re-seeded options + restored selection and the
        // updated "(N)" count labels / disabled state. change.select2 does NOT fire the reload change handler.
        this.control().trigger("change.select2");
    }
}
